import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";
import ImageUploader from "@/components/admin/ImageUploader";

const empty = { name: "", type: "client", logo_url: "", website: "", note: "", order: 0 };

export default function AdminBrands() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/brands");
        setItems(res.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this brand?")) return;
        await api.delete(`/admin/brands/${id}`);
        toast.success("Brand deleted");
        load();
    };

    const filtered = filter === "all" ? items : items.filter((b) => b.type === filter);

    return (
        <div data-testid="admin-brands-page">
            <Header
                title="Clients & Partnerships"
                subtitle="Logo wall yang tampil di halaman publik. Clients = perusahaan tempat Anda bekerja; Partners = platform/tool yang sudah diintegrasikan."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="brand-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New brand
                    </button>
                }
            />

            <div className="mb-5 flex gap-2">
                {["all", "client", "partner"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setFilter(t)}
                        data-testid={`brand-filter-${t}`}
                        className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border ${
                            filter === t ? "bg-brand border-brand text-white" : "border-white/10 text-ink-200 hover:text-white"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="mono-label">Loading…</div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((b) => (
                        <div key={b.id} className="p-4 rounded-xl border border-white/5 bg-ink-800/40 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {b.logo_url ? <img src={b.logo_url} alt={b.name} className="w-full h-full object-contain p-1" /> : <span className="font-display text-lg text-black">{b.name[0]}</span>}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-white font-medium truncate">{b.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-300">{b.type}</div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing({ ...b })} data-testid={`brand-edit-${b.id}`} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"><Pencil size={14} /></button>
                                <button onClick={() => remove(b.id)} data-testid={`brand-delete-${b.id}`} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No brands yet.</div>
                    )}
                </div>
            )}

            {editing && <BrandEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function BrandEditor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const update = (k) => (e) => {
        const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/brands/${initial.id}`, data);
            else await api.post("/admin/brands", data);
            toast.success(isEdit ? "Brand updated" : "Brand created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="brand-editor">
            <div className="w-full max-w-md bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit brand" : "New brand"}</div>
                        <h2 className="font-display text-xl text-white">{data.name || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="brand-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Brand details">
                        <Field label="Name"><input className="ainp" value={data.name} onChange={update("name")} /></Field>
                        <Field label="Type">
                            <select className="ainp" value={data.type} onChange={update("type")}>
                                <option value="client">Client (perusahaan tempat saya bekerja/untuk)</option>
                                <option value="partner">Partner (platform/tool yang saya integrasikan)</option>
                            </select>
                        </Field>
                        <Field label="Logo URL">
                            <ImageUploader value={data.logo_url} onChange={(v) => setData(s => ({...s, logo_url: v}))} placeholder="https://logo.clearbit.com/example.com atau upload..." />
                        </Field>
                        <Field label="Website"><input className="ainp" value={data.website} onChange={update("website")} /></Field>
                        <Field label="Note (untuk partner: e.g. 'Payment gateway')"><input className="ainp" value={data.note} onChange={update("note")} /></Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={update("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
